import React, { useState, useEffect } from 'react';
import progressReportService from '../../services/progressReportService';
import CourseStatusChart from './CourseStatusChart';
import CompletionRateChart from './CompletionRateChart';

/**
 * Component for displaying the overall progress report
 */
const OverallProgressReport: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const data = await progressReportService.getOverallProgressReport();
        setReport(data);
        setError(null);
      } catch (err) {
        setError('Failed to load progress report. Please try again later.');
        console.error('Error fetching progress report:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  if (loading) {
    return <div>Loading progress report...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!report || !report.report) {
    return <div>No report data available.</div>;
  }

  const { summary, customers, generatedAt } = report.report;

  return (
    <div>
      <h2>Overall Progress Report</h2>
      <p>Generated at: {new Date(generatedAt).toLocaleString()}</p>

      <div style={{ marginBottom: '24px' }}>
        <h3>Summary</h3>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          marginBottom: '24px', 
          gap: '20px'
        }}>
          <div style={{ 
            flex: '1 1 400px', 
            minWidth: '300px',
            marginBottom: '16px'
          }}>
            <h4>Course Status Distribution</h4>
            <div style={{ 
              height: '250px',
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <CourseStatusChart 
                completedCourses={summary.completedCourses}
                inProgressCourses={summary.inProgressCourses}
                notStartedCourses={summary.notStartedCourses}
                notCompletedCourses={summary.notCompletedCourses || 0}
              />
            </div>
          </div>
          <div style={{ 
            flex: '1 1 400px', 
            minWidth: '300px',
            marginBottom: '16px'
          }}>
            <h4>Overall Completion Rate</h4>
            <div style={{ 
              height: '250px',
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <CompletionRateChart 
                completionRate={summary.completionRate}
              />
            </div>
          </div>
        </div>
        
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Customers</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Users</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completed Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>In Progress Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Started Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Completed Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.totalCustomers}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.totalUsers}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.totalCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.completedCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.inProgressCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.notStartedCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.notCompletedCourses || 0}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.completionRate.toString().replace(',', '.')}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <h3>Customer Reports</h3>
        {customers.map((customerData: any, index: number) => (
          <div key={index} style={{ marginBottom: '16px', padding: '16px', border: '1px solid #ddd', borderRadius: '4px' }}>
            <h4>{customerData.customer.customerName}</h4>
            <p>Customer Number: {customerData.customer.customerNumber}</p>
            {customerData.customer.primaryContactEmail && (
              <p>Primary Contact: {customerData.customer.primaryContactEmail}</p>
            )}
            
            <div style={{ marginTop: '8px' }}>
              <h5>Summary</h5>
              
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                marginBottom: '16px',
                gap: '20px'
              }}>
                <div style={{ 
                  flex: '1 1 400px', 
                  minWidth: '300px',
                  marginBottom: '16px'
                }}>
                  <h6>Course Status Distribution</h6>
                  <div style={{ 
                    height: '250px',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    <CourseStatusChart 
                      completedCourses={customerData.summary.completedCourses}
                      inProgressCourses={customerData.summary.inProgressCourses}
                      notStartedCourses={customerData.summary.notStartedCourses}
                      notCompletedCourses={customerData.summary.notCompletedCourses || 0}
                    />
                  </div>
                </div>
                <div style={{ 
                  flex: '1 1 400px', 
                  minWidth: '300px',
                  marginBottom: '16px'
                }}>
                  <h6>Completion Rate</h6>
                  <div style={{ 
                    height: '250px',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    <CompletionRateChart 
                      completionRate={customerData.summary.completionRate}
                    />
                  </div>
                </div>
              </div>
              
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Users</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Courses</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completed Courses</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>In Progress Courses</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Started Courses</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Completed Courses</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.totalUsers}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.totalCourses}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.completedCourses}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.inProgressCourses}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.notStartedCourses}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.notCompletedCourses || 0}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{customerData.summary.completionRate.toString().replace(',', '.')}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverallProgressReport;
